import { tokenizeDisplayUnits } from './htmlMarkers';

/**
 * Format persisted draft edit time for tooltips.
 * Today: "11:42 AM"; otherwise "Oct 26, 11:42 AM".
 */
export function formatEditTimestamp(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  if (isToday) return time;
  const monthDay = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${monthDay}, ${time}`;
}

export function buildEditTooltipLabel(editedByName, editedBy, editedAt) {
  const who = editedByName || editedBy || 'User';
  const when = formatEditTimestamp(editedAt);
  return when ? `Edited by ${who} • ${when}` : `Edited by ${who}`;
}

function tokenizeForDiff(text, useMarkerUnits) {
  if (!text) return [];
  return useMarkerUnits ? tokenizeDisplayUnits(text) : Array.from(text);
}

/**
 * Longest common prefix/suffix diff returning [start, end) in the "after" token stream.
 */
export function computeChangedSpan(before, after, { useMarkerUnits = false } = {}) {
  const prev = tokenizeForDiff(before, useMarkerUnits);
  const next = tokenizeForDiff(after, useMarkerUnits);
  let start = 0;
  while (start < prev.length && start < next.length && prev[start] === next[start]) {
    start += 1;
  }
  let endPrev = prev.length;
  let endNext = next.length;
  while (endPrev > start && endNext > start && prev[endPrev - 1] === next[endNext - 1]) {
    endPrev -= 1;
    endNext -= 1;
  }
  if (start >= endNext) return null;
  return { start, end: endNext };
}

export function mergeDraftFieldsFromResponse(response) {
  if (!response || typeof response !== 'object') return {};
  return {
    draftStatus: response.draftStatus || '',
    draftTarget: response.draftTarget || '',
    draftEditedBy: response.draftEditedBy || '',
    draftEditedByName: response.draftEditedByName || '',
    draftEditedAt: response.draftEditedAt || '',
    draftLastEditPath: response.draftLastEditPath || '',
    draftLastEditStart: response.draftLastEditStart ?? null,
    draftLastEditEnd: response.draftLastEditEnd ?? null,
  };
}

/** Merge draft fields onto a request row (list row or detail payload). */
export function patchRequestWithDraftFields(request, draftFieldsOrResponse) {
  if (!request) return request;
  const fields = draftFieldsOrResponse?.draftStatus !== undefined
    || draftFieldsOrResponse?.draftTarget !== undefined
    ? draftFieldsOrResponse
    : mergeDraftFieldsFromResponse(draftFieldsOrResponse);
  return { ...request, ...fields };
}

export function clearDraftFields() {
  return {
    draftStatus: '',
    draftTarget: '',
    draftEditedBy: '',
    draftEditedByName: '',
    draftEditedAt: '',
    draftLastEditPath: '',
    draftLastEditStart: null,
    draftLastEditEnd: null,
  };
}
